import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';

const PhoneScreen = ({ activeStep }) => {
    return (
        <div className="bg-white w-full h-full rounded-[2rem] overflow-hidden relative flex flex-col">
            {/* Status Bar */}
            <div className="h-6 bg-gray-100 flex justify-between items-center px-4 text-[10px] text-gray-500">
                <span>9:41</span>
                <div className="flex gap-1">
                    <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                    <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                </div>
            </div>

            {/* App Header */}
            <div className="h-12 bg-blue-600 flex items-center justify-between px-4 text-white shadow-md z-10">
                <div className="font-bold">SwiftBidPro</div>
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-xs">JS</div>
            </div>

            {/* Dynamic Content Area */}
            <div className="flex-1 bg-gray-50 relative overflow-hidden p-4">
                <AnimatePresence mode="wait">
                    {activeStep === 0 && (
                        <motion.div
                            key="step1"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="space-y-3"
                        >
                            <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                                <div className="text-xs text-gray-400 mb-1">Client</div>
                                <div className="font-semibold text-gray-800">Smith Residence</div>
                            </div>
                            <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                                <div className="flex justify-between items-center mb-2">
                                    <div className="text-xs text-gray-400">Line Items</div>
                                    <div className="text-xs text-blue-600 font-medium">+ Add Item</div>
                                </div>
                                <div className="space-y-2">
                                    <motion.div
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.2 }}
                                        className="flex justify-between text-sm"
                                    >
                                        <span>Interior Paint (Walls)</span>
                                        <span className="font-medium">$1,200</span>
                                    </motion.div>
                                    <motion.div
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.4 }}
                                        className="flex justify-between text-sm"
                                    >
                                        <span>Trim & Baseboards</span>
                                        <span className="font-medium">$450</span>
                                    </motion.div>
                                    <motion.div
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.6 }}
                                        className="flex justify-between text-sm pt-2 border-t"
                                    >
                                        <span className="font-bold">Total</span>
                                        <span className="font-bold text-blue-600">$1,650</span>
                                    </motion.div>
                                </div>
                            </div>
                            <motion.button
                                initial={{ scale: 0.9 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.8, type: "spring" }}
                                className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium text-sm shadow-lg mt-4"
                            >
                                Review Quote
                            </motion.button>
                        </motion.div>
                    )}

                    {activeStep === 1 && (
                        <motion.div
                            key="step2"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="h-full flex flex-col"
                        >
                            <div className="bg-white flex-1 rounded-lg shadow-sm border border-gray-200 p-4 mb-4 relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-full h-1 bg-blue-600"></div>
                                <div className="text-center mb-4 mt-2">
                                    <div className="w-12 h-12 bg-gray-100 rounded-full mx-auto mb-2 flex items-center justify-center text-xl">🏠</div>
                                    <h3 className="font-bold text-lg">Quote #1024</h3>
                                    <p className="text-gray-500 text-xs">Prepared for Smith Residence</p>
                                </div>
                                <div className="space-y-2 mb-4">
                                    <div className="h-2 bg-gray-100 rounded w-3/4"></div>
                                    <div className="h-2 bg-gray-100 rounded w-full"></div>
                                    <div className="h-2 bg-gray-100 rounded w-5/6"></div>
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl font-bold text-gray-900">$1,650.00</div>
                                </div>
                            </div>
                            <motion.div
                                initial={{ y: 50, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.3 }}
                                className="bg-green-500 text-white p-3 rounded-lg text-center font-medium shadow-lg flex items-center justify-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                                Quote Sent!
                            </motion.div>
                        </motion.div>
                    )}

                    {activeStep === 2 && (
                        <motion.div
                            key="step3"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex flex-col items-center justify-center h-full text-center"
                        >
                            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100 w-full">
                                <h3 className="font-bold text-gray-800 mb-4">Sign to Accept</h3>
                                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg h-32 mb-4 relative flex items-center justify-center">
                                    <motion.svg
                                        initial={{ pathLength: 0, opacity: 0 }}
                                        animate={{ pathLength: 1, opacity: 1 }}
                                        transition={{ duration: 1.5, ease: "easeInOut" }}
                                        viewBox="0 0 200 100"
                                        className="w-full h-full absolute top-0 left-0 p-4 text-blue-600"
                                    >
                                        <motion.path
                                            d="M20,50 C50,20 80,80 120,40 S180,60 180,60"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            strokeLinecap="round"
                                        />
                                    </motion.svg>
                                    <span className="text-gray-400 text-sm">Client Signature</span>
                                </div>
                                <motion.button
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 1.6 }}
                                    className="w-full bg-green-600 text-white py-2 rounded-lg font-bold shadow-md"
                                >
                                    Accept Quote
                                </motion.button>
                            </div>
                        </motion.div>
                    )}

                    {activeStep === 3 && (
                        <motion.div
                            key="step4"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex flex-col h-full"
                        >
                            <div className="bg-green-50 p-4 rounded-xl border border-green-100 mb-4 flex items-center gap-3">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600">✓</div>
                                <div>
                                    <div className="font-bold text-green-800">Quote Accepted</div>
                                    <div className="text-xs text-green-600">Just now</div>
                                </div>
                            </div>

                            <div className="flex-1 flex items-center justify-center">
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h10a2 2 0 012 2v14a2 2 0 01-2 2zM7 9h10M7 13h10"></path></svg>
                                    Convert to Invoice
                                </motion.button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Navigation Bar */}
            <div className="h-14 bg-white border-t border-gray-200 flex justify-around items-center px-2">
                <div className="flex flex-col items-center gap-1 text-blue-600">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg>
                    <span className="text-[10px] font-medium">Home</span>
                </div>
                <div className="flex flex-col items-center gap-1 text-gray-400">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path></svg>
                    <span className="text-[10px] font-medium">Quotes</span>
                </div>
                <div className="flex flex-col items-center gap-1 text-gray-400">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    <span className="text-[10px] font-medium">Invoices</span>
                </div>
            </div>
        </div>
    );
};

const InteractivePhoneSection = () => {
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"]
    });

    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        const unsubscribe = scrollYProgress.onChange(v => {
            // Only update when crossing thresholds to reduce re-renders
            const newStep = Math.min(Math.floor(v * 4), 3);
            setActiveStep(prev => prev !== newStep ? newStep : prev);
        });
        return () => unsubscribe();
    }, [scrollYProgress]);

    const steps = [
        {
            title: "Tap. Tap. Done.",
            description: "Build a quote in 60 seconds. Add line items from your saved templates. No typing required.",
            highlight: "The 60-Second Quote"
        },
        {
            title: "Look Like a Pro",
            description: "Send a professional web link, not a messy text. Your clients see a branded proposal that looks great on any device.",
            highlight: "The Professional Send"
        },
        {
            title: "Seal the Deal",
            description: "Clients can sign digitally right on their phone. No printing, no scanning, no 'I'll get back to you'.",
            highlight: "The Digital Handshake"
        },
        {
            title: "Paperwork: Done.",
            description: "Turn that accepted quote into an invoice instantly. Finish the job and the admin work at the same time.",
            highlight: "Instant Invoicing"
        }
    ];

    return (
        <section ref={containerRef} className="relative h-[300vh] md:h-[500vh] bg-gray-50">
            <div className="sticky top-0 h-[100dvh] flex items-center overflow-hidden will-change-transform">
                <div className="container mx-auto px-4 h-full flex flex-col md:flex-row items-center justify-between md:justify-center gap-2 md:gap-0 relative">

                    {/* Left Side: Text Content */}
                    <div className="w-full md:w-1/2 flex flex-col justify-center z-10 pointer-events-none md:pr-12 flex-shrink will-change-contents pt-6">
                        <div className="relative">
                            <div className="w-full" style={{ transform: 'translate3d(0,0,0)' }}>
                                <div className="text-blue-600 font-bold tracking-wider uppercase text-sm mb-4">
                                    {steps[activeStep].highlight}
                                </div>
                                <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-3 md:mb-6 leading-tight">
                                    {steps[activeStep].title}
                                </h2>
                                <p className="text-base md:text-xl text-gray-600 leading-relaxed max-w-lg">
                                    {steps[activeStep].description}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Right Side: Phone Mockup */}
                    <div className="w-full md:w-1/2 flex items-center justify-center relative z-20 flex-shrink-0 will-change-transform" style={{ transform: 'translate3d(0,0,0)' }}>
                        <div className="scale-75 md:scale-100 relative w-[300px] h-[600px] bg-gray-900 rounded-[2.5rem] border-[14px] border-gray-900 shadow-2xl overflow-hidden ring-1 ring-gray-900/50">
                            {/* Phone Buttons */}
                            <div className="absolute top-24 -left-[17px] w-[3px] h-12 bg-gray-800 rounded-l-lg"></div>
                            <div className="absolute top-40 -left-[17px] w-[3px] h-12 bg-gray-800 rounded-l-lg"></div>
                            <div className="absolute top-28 -right-[17px] w-[3px] h-16 bg-gray-800 rounded-r-lg"></div>

                            {/* Screen Content */}
                            <PhoneScreen activeStep={activeStep} />
                        </div>

                        {/* Decorative Background Blob */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-100 rounded-full blur-3xl -z-10 opacity-50"></div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default InteractivePhoneSection;
